import React, { useEffect, useMemo, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import { View, Image, Text } from '@react-pdf/renderer';
import PropTypes from 'prop-types';

Chart.register(...registerables);

const generateRadialQuadrantChart = (originalData, isClicked) => {
  return new Promise((resolve, reject) => {
    if (!originalData || !Array.isArray(originalData)) {
      reject(new Error('Invalid data format: data must be an array'));
      return;
    }

    if (typeof window !== 'undefined' && window.document) {
      const canvas = window.document.createElement('canvas');
      canvas.width = 600;
      canvas.height = 400;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.error("Canvas context not found!");
        reject(new Error('Could not get canvas context'));
        return;
      }

      const colors = ['#C99212', '#B6111F', '#00577D', '#667F96'];
      const labels = ['Strength', 'Heart', 'Soul', 'Mind'];

      const data = [...originalData];

      ctx.save();
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const maxRadius = 180;
      const whiteLineWidth = 12;

      // Nonlinear radius calculation: use sqrt for a sublinear increase
      // This means if value is doubled, the radius increases by sqrt(2) ~= 1.41 times
      const maxValue = Math.max(...data);
      const angles = [-Math.PI / 2, 0, Math.PI / 2, Math.PI];

      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.strokeStyle = 'white';
      ctx.lineWidth = whiteLineWidth;

      data.forEach((value, index) => {
        const minRadius = 65;

        // Nonlinear mapping: use square root, remapped to [minRadius, maxRadius]
        // Normalize value within [0, maxValue], then sqrt, then scale to [minRadius, maxRadius]
        let normalized = maxValue > 0 ? value / maxValue : 0;
        // optional: avoid negatives, clamp to [0,1]
        normalized = Math.max(0, Math.min(normalized, 1));
        // Apply nonlinear mapping
        const nonlinear = Math.sqrt(normalized); // Could use Math.log1p(normalized*10) for a different effect

        // Map nonlinear [0,1] to [minRadius, maxRadius]
        const radius = minRadius + (maxRadius - minRadius) * nonlinear;

        const startAngle = angles[index];
        const endAngle = startAngle + (Math.PI / 2);

        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        ctx.lineTo(centerX, centerY);
        ctx.closePath();
        ctx.fillStyle = colors[index];
        ctx.fill();

        const midAngle = (startAngle + endAngle) / 2;
        // Use fixed offset from center instead of radius-based positioning
        const offsetX = 70; // Horizontal distance from center
        const offsetY = 50; // Vertical distance from center (slightly less for better alignment)
        const textX = centerX + offsetX * Math.cos(midAngle);
        const textY = centerY + offsetY * Math.sin(midAngle);

        ctx.fillStyle = '#FFFFFF';
        ctx.font = '500 18px Helvetica';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const label = `${(Number(value) || 0).toFixed(2)}%`;
        ctx.fillText(label, textX, textY);
      });

      // Draw uniform white separator lines from center to outer edge
      ctx.strokeStyle = 'white';
      ctx.lineWidth = whiteLineWidth;
      angles.forEach((angle) => {
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        const outerX = centerX + maxRadius * Math.cos(angle);
        const outerY = centerY + maxRadius * Math.sin(angle);
        ctx.lineTo(outerX, outerY);
        ctx.stroke();
      });

      ctx.fillStyle = '#343333';
      ctx.font = '500 20px Helvetica';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.fillText('Mind', centerX - maxRadius - 20, centerY - maxRadius);
      ctx.fillText('Soul', centerX - maxRadius - 20, centerY + maxRadius);
      ctx.fillText('Strength', centerX + maxRadius + 20, centerY - maxRadius);
      ctx.fillText('Heart', centerX + maxRadius + 20, centerY + maxRadius);

      ctx.restore();
      resolve(canvas.toDataURL('image/png'));
    } else {
      reject(new Error('Window or document not available'));
    }
  });
};

const GroupStarterChart = ({ originalData, isClicked }) => {
  const [chartImage, setChartImage] = useState(null);
  const [error, setError] = useState(null);
  const dataSignature = useMemo(() => JSON.stringify(originalData ?? []), [originalData]);

  useEffect(() => {
    if (!originalData) {
      setError(new Error('No data provided'));
      console.error("No data provided");
      return;
    }

    generateRadialQuadrantChart(originalData)
      .then((img) => {
        setChartImage(img);
      })
      .catch((err) => {
        console.error("Chart generation error:", err);
        setError(err);
      });
  }, [dataSignature]);

  if (error) {
    return (
      <View style={{ display: 'flex', justifyContent: 'left', alignItems: 'left' }}>
        <Text>Error generating chart: {error.message}</Text>
      </View>
    );
  }

  return (
    <View style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      margin: 0,
      padding: 0
    }}>
      {chartImage && isClicked !== undefined ? (
        <Image 
          src={chartImage} 
          alt=''
          style={{ 
            width: 350,  
            height: 240, 
            display: 'flex',
            alignSelf: 'flex-start',
            justifyContent: "center",
            alignItems: "center",
            margin: 0,
            padding: 0
          }} 
        />
      )
        : 
        (
        <img 
          src={chartImage} 
          alt=''
          style={{ 
            width: 350,  
            height: 240, 
            display: 'flex',
            alignSelf: 'flex-start',
            justifyContent: "center",
            alignItems: "center",
            margin: 0,
            padding: 0
          }}
        />
      )}
    </View>
  );
};

GroupStarterChart.propTypes = {
  originalData: PropTypes.arrayOf(PropTypes.number).isRequired
};

export default GroupStarterChart;
